import { useState, useContext, useEffect } from 'react';
import { parseISO } from 'date-fns';
import { IsoLocale, CountryIdUtil, CurrencyUtil } from '@izettle/constants';
import { UserContext } from '../contexts/user.js';
import { getLocale } from '../utils/get-locale.js';

const defractionize = num => num / 100;
const defaultOptions = {
  minimumFractionDigits: 0
};
const defaultDateOptions = {
  day: "numeric",
  month: "long",
  year: "numeric"
};
const createFormatters = (data, version) => {
  const format = (n, options) => Intl.NumberFormat(data.isoLocale, options).format(n);
  const formatNumber = (n, options) => format(n, {
    ...defaultOptions,
    ...options
  });
  const formatNumberFractionized = (n, options) => format(defractionize(n), {
    ...defaultOptions,
    ...options
  });
  const formatCurrency = (n, options) => format(defractionize(n), {
    ...defaultOptions,
    ...options,
    currency: data.currency,
    style: "currency"
  });
  const formatPercent = (n, options) =>
  // Divide by 10000 since we use Basis Points for percentages.
  // Get 10_000BP from server -> 10_000BP / 100 = 10% -> 10% / 10 -> 1
  format(n / 10000, {
    ...defaultOptions,
    ...options,
    style: "percent"
  });
  const formatDate = (dateInput, options) => {
    const dateValue = typeof dateInput === "string" ? parseISO(dateInput) : dateInput;
    return Intl.DateTimeFormat(data.isoLocale, options ?? defaultDateOptions).format(dateValue);
  };
  return {
    version,
    format,
    formatNumber,
    formatNumberFractionized,
    formatCurrency,
    formatPercent,
    formatDate
  };
};
const localeDataFromIsoLocale = locale => {
  const country = CountryIdUtil.fromIsoLocale(locale);
  if (locale == null || !CountryIdUtil.isSupportedMarket(country)) {
    return null;
  }
  return {
    country,
    currency: CurrencyUtil.fromCountry(country),
    language: locale.slice(0, locale.indexOf("-")),
    isoLocale: locale
  };
};
const useFormatter = () => {
  // `version` is used to check for if we need to re-render hooks and their outputs
  const [version, setVersion] = useState(0);

  // UserContext might not exist, so we cast the type to T | undefined
  const userData = useContext(UserContext);
  const [data, setData] = useState(userData?.locale ?? localeDataFromIsoLocale(getLocale(window.navigator.languages) ?? IsoLocale.enGB));
  const [formatters, setFormatters] = useState(createFormatters(data, 0));
  useEffect(() => {
    if (userData == null) {
      return;
    }
    setVersion(v => v + 1);
    setData(userData.locale);
    setFormatters(createFormatters(userData.locale, version));
  }, [userData?.locale.isoLocale, userData?.locale.country, userData?.locale.currency, userData?.locale.language]);
  return formatters;
};

export { useFormatter };
//# sourceMappingURL=use-formatter.js.map
